// React core
import React, { useState } from "react";

// Third-party libraries
import {
  Calendar,
  CheckCircle,
  PhoneCall,
  Shield,
} from "react-feather";

// App imports
import useAppStore from "../../store/AppStore";
import { useBoundStore } from "../../store/UseBoundStore1";
import {
  currentTelephonicAudioFormat,
} from "../../libs/constants/index";
import rfetch from "../../utils/network/recursive-fetch";
import { BotConstants } from "../../libs/constants";
import * as Sentry from "../../config/sentry";
import { siteVersion } from "../../config/version";

// Assets and styles
import logo from "../../assests/images/logo.png";
import "../../styles/LoginFormNew.css";

class CallStatus {
  static Initial = "initial";
  static Initiating = "initiating";
  static Initiated = "initiated";
  static Failed = "failed";
}

const TelephonicInterviewLogin = ({ interviewData, userData }) => {
  const { setAccessToken } = useAppStore();
  const {
    mId,
    version,
    firstName,
    jobTitle,
    organizationName,
  } = useBoundStore();

  const [preInterviewChecklist, setPreInterviewChecklist] = useState({
    environment: false,
    aiResponseTime: false,
  });
  const [isLoading, setIsLoading] = useState({
    joinInterviewApi: false,
  });
  const [callStatus, setCallStatus] = useState(CallStatus.Initial);

  const handleSubmit = async (event) => {
    setIsLoading((e) => ({ ...e, joinInterviewApi: true }));
    
    if (mId === "") {
      console.log("[TelephonicInterviewLogin] No meetingId found:", { mId });
      return;
    }
    
    if (userData?.username === "") {
      console.log("[TelephonicInterviewLogin] No username found:", { username: userData?.username });
      return;
    }
    try {
      setCallStatus(CallStatus.Initiating);
      const response = await rfetch(
        `${process.env.REACT_APP_BASE_URL}/join_meeting/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            meeting_id: mId,
            user_agent: "twilio",
            candidate_first_name: userData?.candidateFirstName,
            candidate_last_name: userData?.candidateLastName,
            candidate_phone_number: userData?.toPhoneNumber,
            job_title: userData?.candidateJobTitle,
            version: version,
            ext: currentTelephonicAudioFormat.extension,
            media_type: "audio",
          }),
        },
        {
          timeout: 60000,
          maxRetries: 0,
        }
      );
      if (response.ok) {
        const token = await response.json();
        console.log("[TelephonicInterviewLogin] Join meeting successful:", {
          hasAccessToken: !!token.access_token,
          mId,
          version,
          phoneNumber: userData?.toPhoneNumber,
          callStatus: CallStatus.Initiated
        });
        setAccessToken(token.access_token);
        setCallStatus(CallStatus.Initiated);
      } else {
        setCallStatus(CallStatus.Failed);
        const data = await response.json();
        console.error("[TelephonicInterviewLogin] Join meeting failed:", {
          status: response.status,
          statusText: response.statusText,
          data,
          mId,
          username: userData?.username,
          phoneNumber: userData?.toPhoneNumber
        });
      }
    } catch (error) {
      console.error("[TelephonicInterviewLogin] Error during login process:", {
        error: error.message,
        stack: error.stack,
        mId,
        username: userData?.username,
        version,
        phoneNumber: userData?.toPhoneNumber
      });
      Sentry.captureException(error);
      setCallStatus(CallStatus.Failed);
    } finally {
      setIsLoading((e) => ({ ...e, joinInterviewApi: false }));
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-[#f5f7ff] to-[#fafbff] flex justify-center items-center">
        <div className="flex flex-col items-center justify-center w-full">
          <div className="w-48 md:w-56 mb-4">
            <img src={logo} alt="Logo" />
          </div>

          <div className="w-full max-w-3xl bg-white rounded-xl shadow-md p-6">
            <h1 className="text-2xl font-semibold text-center bg-gradient-to-r from-[#4776e6] to-[#8E54E9] text-transparent bg-clip-text">
              Welcome to your Telephonic interview session
              {firstName ? `, ${firstName}!` : ""}
            </h1>
            <p className="hidden md:flex w-full justify-center text-center text-xs text-gray-500 mt-2">
              Please complete the preparation steps below to ensure the best
              interview experience
            </p>

            {/* Stepper */}
            <div className="hidden md:flex items-center justify-center mt-6 space-x-5">
              <div className="flex flex-col items-center text-app-primary">
                <div className="text-sm w-7 h-7 border border-app-primary text-app-primary rounded-full flex items-center justify-center font-semibold">
                  1
                </div>
                <p className="text-[13px] mt-1 font-medium">Pre-Interview</p>
                <p className="text-[10px] text-gray-400">Complete checklist</p>
              </div>
            </div>

            {/* Interview Details */}
            <div className="bg-gradient-to-b from-[#f5f7ff] to-[#fafbff] border border-[#dce3fd] rounded-lg p-4 mt-6">
              <div className="mb-4 flex items-center gap-x-2">
                <Calendar size={16} className="text-app-primary" />
                <p className="text-[#101828] text-sm font-medium">
                  Interview Details
                </p>
              </div>

              <div
                className={`grid grid-cols-1 md:grid-cols-3 gap-4 text-[13px] font-medium text-gray-600`}
              >
                {userData?.username && (
                  <>
                    <div>
                      <p className="text-gray-400 font-normal">Role</p>
                      <p className="text-[#101828] text-xs font-medium">
                        {jobTitle}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 font-normal">Organization</p>
                      <p className="text-[#101828] text-xs font-medium">
                        {organizationName}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 font-normal">
                        Interview Type
                      </p>
                      <div className="flex items-center gap-1 text-[#101828] font-semibold gap-x-2">
                        <PhoneCall size={14} className="text-app-primary" />

                        <p className="text-[#101828] text-xs font-medium">
                          Telephone Interview
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {interviewData?.meetingStatus !== "" && (
                <div className="w-full text-sm text-center text-red-600 ms-5 font-medium">
                  {interviewData?.meetingStatus}
                </div>
              )}
            </div>

            {/* Checklist */}
            <div className="bg-gradient-to-b from-[#f5f7ff] to-[#fafbff] border border-[#dce3fd] rounded-lg px-3 py-4 mt-6">
              <div className="flex items-center justify-start mb-4 gap-x-2">
                <CheckCircle size={16} className="text-app-primary" />
                <h5 className="text-app-primary text-sm font-semibold ">
                  Pre-Interview Checklist
                </h5>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div className="w-full flex items-start gap-2">
                  <div className="w-[8%] flex items-center justify-center">
                    <input
                      type="checkbox"
                      value={preInterviewChecklist.environment}
                      onChange={(event) =>
                        setPreInterviewChecklist((e) => ({
                          ...e,
                          environment: event.target.checked,
                        }))
                      }
                      className="mt-1 accent-app-primary w-4 h-4 cursor-pointer"
                    />
                  </div>
                  <div className="w-[90%]">
                    <p className="font-medium text-[13px] text-app-text">
                      Environment
                    </p>
                    <p className="text-gray-500">
                      Find a quiet room with minimal background noise.
                    </p>
                  </div>
                </div>

                <div className="w-full flex items-start gap-2">
                  <div className="w-[8%] flex items-center justify-center">
                    <input
                      type="checkbox"
                      value={preInterviewChecklist.aiResponseTime}
                      onChange={(event) => {
                        setPreInterviewChecklist((e) => ({
                          ...e,
                          aiResponseTime: event.target.checked,
                        }));
                      }}
                      className="mt-1 accent-app-primary w-4 h-4 cursor-pointer"
                    />
                  </div>
                  <div className="w-[90%]">
                    <p className="font-medium text-[13px] text-app-text">
                      AI Response Time
                    </p>
                    <p className="text-gray-500">
                      AI interviewer make require 2-3 secs to respond back to
                      your answer.
                    </p>
                  </div>
                </div>
              </div>

            </div>

            {/* Continue Button */}

            {callStatus !== CallStatus.Initiated && (
              <div className="mt-8 flex justify-center md:justify-end">
                <button
                  className={`text-sm  font-semibold px-5 py-3 rounded-md w-56 ${
                    userData?.username &&
                    userData?.email &&
                    preInterviewChecklist.aiResponseTime &&
                    preInterviewChecklist.environment
                      ? "bg-app-primary text-white hover:bg-app-primaryHover2 cursor-pointer"
                      : "bg-app-slateGray text-app-darkGrayText cursor-not-allowed hover:bg-app-slateGray"
                  }`}
                  onClick={
                    userData?.username &&
                    userData?.email &&
                    preInterviewChecklist.aiResponseTime &&
                    preInterviewChecklist.environment
                      ? (e) => handleSubmit(e)
                      : () => {}
                  }
                >
                  {isLoading.joinInterviewApi ? (
                    <span className="loading loading-dots loading-sm text-white"></span>
                  ) : (
                    "Click to Receive Call"
                  )}
                </button>
              </div>
            )}
            {callStatus === CallStatus.Initiated && userData?.username && (
              <div className="w-full flex items-center justify-center text-center text-base text-app-primary font-semibold mt-7">
                You'll receive a call from {BotConstants.BOT_NAME} (
                {userData?.fromPhoneNumber})
              </div>
            )}

            {/* --------------------  Note ------------------------*/}
            {preInterviewChecklist.aiResponseTime &&
            preInterviewChecklist.environment ? (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-start gap-x-3 bg-app-successLight text-app-success text-sm px-4 py-3 rounded-md border border-app-successLight">
                <Shield size={19} />
                <div>
                  <p className="text-[13px] font-medium">
                    You're all set to join your interview
                  </p>
                  <p className="text-[10px]">
                    For the best experience, we recommend completing all
                    preparation steps.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-6 flex gap-y-2  flex-col sm:flex-row items-center justify-center sm:justify-start gap-x-3 bg-app-secondaryLight text-app-secondary text-sm px-4 py-3 rounded-md border border-app-secondaryLight">
                <Shield size={19} />
                <div>
                  <p className="text-[13px] font-medium text-center sm:text-start">
                    Complete the checklist before proceeding
                  </p>
                  <p className="text-[10px] text-center sm:text-start">
                    For the best experience, we recommend completing all
                    preparation steps.
                  </p>
                </div>
              </div>
            )}

            {/* Footer */}

            <p className="text-center text-gray-400 font-normal text-xs mt-4">
              {siteVersion}
            </p>
            <p className="text-center text-gray-400 font-normal text-xs mt-2">
              © 2025 SofiaWorks. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default TelephonicInterviewLogin;
